
import React, { useEffect, useState } from "react";
import usersApi, { ReportInterval } from "../services/users/UserApi";
import CommonHeader from "../components/CommonHeader";

interface FeedbackSender {
  id: number;
  firstName: string;
  email: string;
  companyName: string;
  website: string;
  noOfEmployees: number;
  type: string;
  status: string;
}

interface FeedbackItem {
  id: number;
  sender: FeedbackSender;
  subject: string;
  description: string;
}


const FeedbackPage = () => {
  const [activeTab, setActiveTab] = useState<'feedback' | 'suggestions'>('feedback');
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [suggestions, setSuggestions] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interval, setInterval] = useState<ReportInterval>(ReportInterval.DAILY);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  // Removed unused pageNumber state

  const fetchData = async (tab: 'feedback' | 'suggestions', filters?: { interval?: ReportInterval; startDate?: string; endDate?: string }) => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (interval) params.type = interval;
      if (interval === ReportInterval.CUSTOM) {
        if (startDate) params.startTime = new Date(startDate).getTime();
        if (endDate) params.endTime = new Date(endDate).getTime();
      }
      // For demo, use same API for both tabs, but filter by subject for suggestions
      const res = await usersApi.getFeedbackList(params);
      if (res.success && res.value && res.value.data && Array.isArray(res.value.data.contents)) {
        if (tab === 'feedback') {
          setFeedback(res.value.data.contents.filter((item: any) => !item.subject?.toLowerCase().includes('suggestion')));
        } else {
          setSuggestions(res.value.data.contents.filter((item: any) => item.subject?.toLowerCase().includes('suggestion')));
        }
      } else {
        setError("Invalid response from server");
      }
    } catch (err: any) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(activeTab);
    // eslint-disable-next-line
  }, [activeTab]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData(activeTab, { interval, startDate, endDate });
  };

  return (
    <div className="flex w-full h-[calc(100vh-67px)] flex-col">
      <CommonHeader title={activeTab === 'feedback' ? "Feedback" : "Suggestions"} />
      <div className="flex-1 overflow-auto p-8">
      <div className="mb-6 flex gap-4">
        <button
          className={`px-4 py-2 rounded-lg border transition ${activeTab === 'feedback' ? 'bg-violet-600 text-white border-violet-600' : 'bg-ez-surface text-ez-text-secondary border-ez-border hover:bg-ez-elevated'}`}
          onClick={() => setActiveTab('feedback')}
        >
          Feedback
        </button>
        <button
          className={`px-4 py-2 rounded-lg border transition ${activeTab === 'suggestions' ? 'bg-violet-600 text-white border-violet-600' : 'bg-ez-surface text-ez-text-secondary border-ez-border hover:bg-ez-elevated'}`}
          onClick={() => setActiveTab('suggestions')}
        >
          Suggestions
        </button>
      </div>
      <form className="flex flex-wrap gap-4 mb-6 items-end" onSubmit={handleFilter}>
        <div>
          <label className="block text-xs font-semibold mb-1 text-ez-text-secondary">Interval</label>
          <select
            className="p-2 rounded-lg border bg-ez-surface text-ez-text-primary border-ez-border"
            value={interval}
            onChange={e => setInterval(e.target.value as ReportInterval)}
          >
            {Object.values(ReportInterval).map((val) => (
              <option key={val} value={val}>{val}</option>
            ))}
          </select>
        </div>
        {interval === ReportInterval.CUSTOM && (
          <>
            <div>
              <label className="block text-xs font-semibold mb-1 text-ez-text-secondary">Start Date</label>
              <input
                type="date"
                className="p-2 rounded-lg border bg-ez-surface text-ez-text-primary border-ez-border"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 text-ez-text-secondary">End Date</label>
              <input
                type="date"
                className="p-2 rounded-lg border bg-ez-surface text-ez-text-primary border-ez-border"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
          </>
        )}
        <button type="submit" className="px-4 py-2 bg-violet-600 text-white rounded-lg shadow-sm hover:bg-violet-700 transition">Filter</button>
      </form>
      {loading ? (
        <div className="text-ez-text-secondary">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-hidden border border-ez-border bg-ez-surface shadow-sm">
          <table className="min-w-full">
            <thead className="bg-ez-elevated text-ez-text-secondary text-xs uppercase">
              <tr>
                <th className="py-3 px-4 border-b border-ez-border">ID</th>
                <th className="py-3 px-4 border-b border-ez-border">User</th>
                <th className="py-3 px-4 border-b border-ez-border">Email</th>
                <th className="py-3 px-4 border-b border-ez-border">Subject</th>
                <th className="py-3 px-4 border-b border-ez-border">Description</th>
              </tr>
            </thead>
            <tbody className="text-ez-text-secondary">
              {(activeTab === 'feedback' ? feedback : suggestions).map(item => (
                <tr key={item.id} className="border-b border-ez-border hover:bg-ez-elevated transition-colors">
                  <td className="py-3 px-4 text-center text-ez-text-primary">{item.id}</td>
                  <td className="py-3 px-4 text-ez-text-primary">{item.sender.firstName}</td>
                  <td className="py-3 px-4">{item.sender.email}</td>
                  <td className="py-3 px-4">{item.subject}</td>
                  <td className="py-3 px-4">{item.description}</td>
                </tr>
              ))}
              {(activeTab === 'feedback' ? feedback : suggestions).length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-ez-text-muted">No records found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
};

export default FeedbackPage;



